import { useStyletron } from 'baseui';
const useEditorTheme = () => {
  const [css, theme] = useStyletron();
  const editorTheme = {
    ltr: css({
      textAlign: 'left'
    }),
    rtl: css({
      textAlign: 'right'
    }),
    paragraph: css({
      margin: '0',
      marginBottom: theme.sizing.scale550,
      position: 'relative'
    }),
    text: {
      bold: css({
        fontWeight: 'bold'
      }),
      italic: css({
        fontStyle: 'italic'
      }),
      underline: css({
        textDecoration: 'underline'
      }),
      strikethrough: css({
        textDecoration: 'line-through'
      }),
      underlineStrikethrough: css({
        textDecoration: 'underline line-through'
      }),
      code: css({
        backgroundColor: theme.colors.backgroundTertiary,
        padding: `1px ${theme.sizing.scale100}`,
        fontSize: '94%'
      })
    },
    quote: css({
      margin: `0 0 ${theme.sizing.scale400} ${theme.sizing.scale700}`,
      color: theme.colors.contentSecondary,
      borderLeftColor: theme.colors.borderAccentLight,
      borderLeftWidth: theme.sizing.scale100,
      borderLeftStyle: 'solid',
      paddingLeft: theme.sizing.scale600
    }),
    code: css({
      backgroundColor: theme.colors.backgroundTertiary,
      display: 'block',
      padding: `${theme.sizing.scale300} ${theme.sizing.scale300} ${theme.sizing.scale300} ${theme.sizing.scale1400}`,
      lineHeight: '1.53',
      margin: `${theme.sizing.scale300} 0`,
      overflowX: 'auto',
      position: 'relative',
      tabSize: '2',
      ':before': {
        content: 'attr(data-gutter)',
        position: 'absolute',
        backgroundColor: theme.colors.backgroundTertiary,
        left: '0',
        top: '0',
        borderRight: `1px solid ${theme.colors.borderOpaque}`,
        padding: theme.sizing.scale300,
        color: theme.colors.backgroundAccent,
        whiteSpace: 'pre-wrap',
        textAlign: 'right',
        minWidth: theme.sizing.scale800
      }
    }),
    link: css({
      textDecoration: 'none',
      ':hover': {
        textDecoration: 'underline',
        cursor: 'pointer'
      }
    }),
    list: {
      listitem: css({
        margin: `0 ${theme.sizing.scale900}`
      }),
      listitemChecked: css({
        position: 'relative',
        marginLeft: theme.sizing.scale300,
        marginRight: theme.sizing.scale300,
        paddingLeft: theme.sizing.scale800,
        paddingRight: theme.sizing.scale800,
        listStyleType: 'none',
        outline: 'none',
        textDecoration: 'line-through',
        ':before': {
          content: '""',
          width: theme.sizing.scale600,
          height: theme.sizing.scale600,
          top: theme.sizing.scale0,
          left: '0',
          cursor: 'pointer',
          display: 'block',
          backgroundSize: 'cover',
          position: 'absolute',
          border: `1px solid ${theme.colors.backgroundInverseSecondary}`,
          borderRadius: theme.sizing.scale0,
          backgroundColor: theme.colors.backgroundInverseSecondary,
          backgroundRepeat: 'no-repeat'
        },
        "[dir='rtl']:before": {
          left: 'auto',
          right: '0'
        },
        ':focus:before': {
          boxShadow: `0 0 0 ${theme.sizing.scale0} ${theme.colors.borderAccentLight}`,
          borderRadius: theme.sizing.scale0
        },
        ':after': {
          content: '""',
          cursor: 'pointer',
          borderColor: theme.colors.borderOpaque,
          borderStyle: 'solid',
          position: 'absolute',
          display: 'block',
          top: theme.sizing.scale200,
          width: '3px',
          left: '7px',
          right: '7px',
          height: theme.sizing.scale200,
          transform: 'rotate(45deg)',
          borderWidth: `0 ${theme.sizing.scale0} ${theme.sizing.scale0} 0`
        }
      }),
      listitemUnchecked: css({
        position: 'relative',
        marginLeft: theme.sizing.scale300,
        marginRight: theme.sizing.scale300,
        paddingLeft: theme.sizing.scale800,
        paddingRight: theme.sizing.scale800,
        listStyleType: 'none',
        outline: 'none',
        ':before': {
          content: '""',
          width: theme.sizing.scale600,
          height: theme.sizing.scale600,
          top: theme.sizing.scale0,
          left: '0',
          cursor: 'pointer',
          display: 'block',
          backgroundSize: 'cover',
          position: 'absolute',
          border: `1px solid ${theme.colors.backgroundInverseSecondary}`,
          borderRadius: theme.sizing.scale0
        },
        "[dir='rtl']:before": {
          left: 'auto',
          right: '0'
        },
        ':focus:before': {
          boxShadow: `0 0 0 ${theme.sizing.scale0} ${theme.colors.borderAccentLight}`,
          borderRadius: theme.sizing.scale0
        }
      }),
      nested: {
        listitem: css({
          listStyleType: 'none',
          ':before, :after': {
            display: 'none'
          }
        })
      },
      olDepth: [css({
        padding: 0,
        margin: 0,
        listStylePosition: 'outside'
      }), css({
        padding: 0,
        margin: 0,
        listStyleType: 'upper-alpha',
        listStylePosition: 'outside'
      }), css({
        padding: 0,
        margin: 0,
        listStyleType: 'lower-alpha',
        listStylePosition: 'outside'
      }), css({
        padding: 0,
        margin: 0,
        listStyleType: 'upper-roman',
        listStylePosition: 'outside'
      }), css({
        padding: 0,
        margin: 0,
        listStyleType: 'lower-roman',
        listStylePosition: 'outside'
      })],
      ul: css({
        padding: 0,
        margin: 0,
        listStylePosition: 'outside'
      })
    },
    codeHighlight: {
      atrule: css({
        color: '#07a'
      }),
      attr: css({
        color: '#07a'
      }),
      boolean: css({
        color: '#905'
      }),
      builtin: css({
        color: '#690'
      }),
      cdata: css({
        color: 'slategray'
      }),
      char: css({
        color: '#690'
      }),
      class: css({
        color: '#dd4a68'
      }),
      'class-name': css({
        color: '#dd4a68'
      }),
      comment: css({
        color: 'slategray'
      }),
      constant: css({
        color: '#905'
      }),
      deleted: css({
        color: '#905'
      }),
      doctype: css({
        color: 'slategray'
      }),
      entity: css({
        color: '#9a6e3a'
      }),
      function: css({
        color: '#dd4a68'
      }),
      important: css({
        color: '#e90'
      }),
      inserted: css({
        color: '#690'
      }),
      keyword: css({
        color: '#07a'
      }),
      namespace: css({
        color: '#e90'
      }),
      number: css({
        color: '#905'
      }),
      operator: css({
        color: '#9a6e3a'
      }),
      prolog: css({
        color: 'slategray'
      }),
      property: css({
        color: '#905'
      }),
      punctuation: css({
        color: '#999'
      }),
      regex: css({
        color: '#e90'
      }),
      selector: css({
        color: '#690'
      }),
      string: css({
        color: '#690'
      }),
      symbol: css({
        color: '#905'
      }),
      tag: css({
        color: '#905'
      }),
      url: css({
        color: '#9a6e3a'
      }),
      variable: css({
        color: '#e90'
      })
    }
  };
  return editorTheme;
};
export default useEditorTheme;